import { useEffect, useMemo, useState } from "react";
import styles from "../WorkoutAdminModal.module.css";
import { useToast } from "../ToastProvider";

const DAYS = ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"];
const DAYS_SHORT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const LETTERS = "ABCDEFGHIJKLMN";

/** Sugestão de dias pra N treinos: espalha pela semana (seg/qua/sex pra 3). */
export function suggestDays(n) {
  const presets = { 1: [0], 2: [0, 3], 3: [0, 2, 4], 4: [0, 1, 3, 4], 5: [0, 1, 2, 3, 4], 6: [0, 1, 2, 3, 4, 5], 7: [0, 1, 2, 3, 4, 5, 6] };
  return Array.from({ length: n }, (_, i) => (presets[n] ? presets[n][i] : null));
}

const isActivePlan = (p) => p.is_active !== false;

/** Dias na ordem do programa: só os treinos ativos recebem sugestão (o
 * inativo não vai pro aluno), e o array fica alinhado com a posição. */
export function suggestDaysForPlans(plans) {
  const active = suggestDays(plans.filter(isActivePlan).length);
  let i = 0;
  return plans.map((p) => (isActivePlan(p) ? active[i++] : null));
}

/**
 * Atribuir um programa inteiro ao aluno (todos os treinos, na ordem, cada um
 * com o seu dia). Fica dentro do card de atribuição da aba Alunos, como
 * alternativa a "Um treino". Se o aluno já tem programa em andamento, oferece
 * encerrar esse programa na troca (desativa só os treinos dele).
 */
export default function StudentProgramAssign({ student, programs, history, assignProgram, onAssigned }) {
  const toast = useToast();
  const [programId, setProgramId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [days, setDays] = useState([]);
  const [endCurrent, setEndCurrent] = useState(true);
  const [busy, setBusy] = useState(false);

  const usable = useMemo(() => programs.filter((p) => (p.plans || []).some(isActivePlan)), [programs]);
  const program = usable.find((p) => String(p.id) === String(programId)) || null;
  const current = useMemo(() => history.filter((h) => h.in_progress), [history]);

  useEffect(() => { setProgramId(""); setStartDate(""); setDays([]); setEndCurrent(true); }, [student?.id]);

  const pickProgram = (id) => {
    setProgramId(id);
    const p = usable.find((x) => String(x.id) === String(id));
    setDays(p ? suggestDaysForPlans(p.plans) : []);
  };

  const activeCount = program ? program.plans.filter(isActivePlan).length : 0;
  const inactive = program ? program.plans.filter((p) => !isActivePlan(p)) : [];

  const sameDay = useMemo(() => {
    const seen = new Set();
    const dup = new Set();
    days.forEach((d) => { if (d != null) (seen.has(d) ? dup : seen).add(d); });
    return [...dup];
  }, [days]);

  const handleAssign = async () => {
    if (!program) return;
    setBusy(true);
    try {
      await assignProgram(program.id, {
        userIds: [student.id],
        startDate,
        daysOfWeek: days,
        endCurrent: current.length > 0 && endCurrent,
      });
      toast.success(
        `Programa "${program.name}" atribuído com ${activeCount} ${activeCount === 1 ? "treino" : "treinos"}.`
        + (inactive.length ? ` Ficou de fora (inativo): ${inactive.map((p) => p.title).join(", ")}.` : "")
      );
      setProgramId("");
      setDays([]);
      setStartDate("");
      await onAssigned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível atribuir o programa.");
    } finally {
      setBusy(false);
    }
  };

  if (!usable.length) {
    return (
      <div className={styles.wamEmpty}>
        Você ainda não tem programas com treinos. Na aba Treinos, use "Novo programa" para reunir os treinos de um método (ex: Push, Pull e Legs) e atribuir todos de uma vez.
      </div>
    );
  }

  return (
    <>
      <div className={styles.wamFormGrid}>
        <div className={styles.wamField}>
          <label htmlFor="student-program-select">Programa</label>
          <select id="student-program-select" value={programId} onChange={(e) => pickProgram(e.target.value)}>
            <option value="">Escolha um programa</option>
            {usable.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.method ? ` (${p.method})` : ""} - {p.plans.filter(isActivePlan).length} treinos
              </option>
            ))}
          </select>
        </div>
        <div className={styles.wamField}>
          <label htmlFor="student-program-start">Início <span className={styles.programOptional}>(opcional)</span></label>
          <input id="student-program-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
      </div>

      {program && (
        <div className={styles.wamField}>
          <label id="student-program-days">Dia de cada treino</label>
          <ol className={styles.programOrderList} aria-labelledby="student-program-days">
            {program.plans.map((plan, idx) => (
              <li key={plan.id} className={`${styles.programOrderRow} ${isActivePlan(plan) ? "" : styles.programOrderRowOff}`}>
                <span className={styles.programLetter} aria-hidden="true">{LETTERS[idx] || idx + 1}</span>
                <span className={styles.programOrderTitle}>{plan.title}</span>
                {!isActivePlan(plan) ? (
                  <span className={styles.programHint}>Inativo, não será atribuído</span>
                ) : (
                <select
                  aria-label={`Dia sugerido para ${plan.title}`}
                  value={days[idx] ?? ""}
                  onChange={(e) => {
                    const v = e.target.value === "" ? null : Number(e.target.value);
                    setDays((prev) => prev.map((d, i) => (i === idx ? v : d)));
                  }}
                >
                  <option value="">Sem dia</option>
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
                )}
              </li>
            ))}
          </ol>
          {inactive.length > 0 && (
            <p className={styles.programHint} role="status">
              {inactive.length === 1
                ? `${inactive[0].title} está inativo e fica de fora. Para incluir, reative o treino na aba Treinos.`
                : `${inactive.length} treinos inativos ficam de fora. Para incluir, reative na aba Treinos.`}
            </p>
          )}
          {sameDay.length > 0 && (
            <p className={styles.programHint}>
              Mais de um treino em {sameDay.map((d) => DAYS_SHORT[d]).join(", ")}. Tudo bem se for de propósito.
            </p>
          )}
        </div>
      )}

      {program && current.length > 0 && (
        <label className={styles.wamCheck}>
          <input type="checkbox" checked={endCurrent} onChange={(e) => setEndCurrent(e.target.checked)} />
          <span>
            Encerrar {current.length === 1 ? `o programa atual (${current[0].program_name})` : `os ${current.length} programas em andamento`} e desativar os treinos dele.
            {" "}Treinos avulsos continuam como estão.
          </span>
        </label>
      )}

      <div className={styles.actionsRow}>
        <button type="button" className={`${styles.wamBtn} ${styles.primary}`} onClick={handleAssign} disabled={busy || !program}>
          {program ? `Atribuir ${activeCount} ${activeCount === 1 ? "treino" : "treinos"}` : "Atribuir programa"}
        </button>
      </div>
    </>
  );
}
